<template>
    <div>
        <Dialog v-model:visible="visible" modal :header="authStore.user.two_factor_enabled ? $t('dialogs.two_factor_auth_dialog.show_header') : $t('dialogs.two_factor_auth_dialog.enable_header')" v-if="step==1">

            <template #header>
                <div class="inline-flex align-items-center justify-content-center gap-2">
                    <!-- <IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/> -->
                    <span class="text-lg font-semibold leading-6 text-gray-900">{{ authStore.user.two_factor_enabled ? $t('dialogs.two_factor_auth_dialog.show_header') : $t('dialogs.two_factor_auth_dialog.enable_header') }}</span>
                </div>
            </template>            

            <div>
                {{ $t('dialogs.two_factor_auth_dialog.description') }}<br />
                <br />
                {{ $t('dialogs.two_factor_auth_dialog.login_description') }}<br />
                <br />
                <strong>{{ $t('dialogs.two_factor_auth_dialog.recommendation') }}</strong><br />
                <br />
                <div class="bg-gray-100 p-4 m-4">
                    <strong>{{ $t('dialogs.two_factor_auth_dialog.please_note') }}</strong><br />
                    {{ $t('dialogs.two_factor_auth_dialog.authenticator_app_instructions') }}<br />
                    <br />
                    <strong>Google Authenticator</strong> - <a class="underline" target="_blank" href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_GB&pli=1">Android</a> / <a class="underline" href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank">iOS</a><br />
                    <br />
                    <strong>Microsoft Authenticator</strong> - <a class="underline" target="_blank" href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en_GB">Android</a> / <a class="underline" href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank">iOS</a> <br />
                </div>
            </div>
            
            <template #footer>
                <!-- <Button :label="$t('common.cancel')" text severity="secondary" @click="cancel"/> -->
                <Button :label="authStore.user.two_factor_enabled ? $t('dialogs.two_factor_auth_dialog.show_two_factor') : $t('dialogs.two_factor_auth_dialog.setup_two_factor')" @click="nextStep()" autofocus />
            </template>

        </Dialog>
        
        <Dialog v-model:visible="visible" modal :header="$t('dialogs.two_factor_auth_dialog.scan_qr_code_header')" v-if="step==2">

            <template #header>
                <div class="inline-flex align-items-center justify-content-center gap-2">
                    <!-- <IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/> -->
                    <span class="text-lg font-semibold leading-6 text-gray-900">{{ $t('dialogs.two_factor_auth_dialog.scan_qr_code_header') }}</span>
                </div>
            </template>            

            <div>
                {{ $t('dialogs.two_factor_auth_dialog.scan_qr_code_instructions') }}
                <div class="p-6 text-center min-h-400" id="qr-code-container">
                    <img :alt="$t('dialogs.two_factor_auth_dialog.qr_code_alt')" />
                </div>
                <div class="p-6 text-center min-h-400" id="qr-code-spinner-container">
                    <ProgressSpinner />
                </div>
            </div>

            <template #footer>
                <!-- <Button :label="$t('common.cancel')" text severity="secondary" @click="cancel"/> -->
                <Button :label="$t('common.back')" outlined severity="secondary" @click="prevStep()" />
                <Button :label="$t('dialogs.two_factor_auth_dialog.qr_code_scanned')" @click="nextStep()" />
            </template>
            
        </Dialog>
        
        <Dialog v-model:visible="visible" modal :header="$t('dialogs.two_factor_auth_dialog.enter_code_header')" v-if="step==3">

            <template #header>
                <div class="inline-flex align-items-center justify-content-center gap-2">
                    <!-- <IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/> -->
                    <span class="text-lg font-semibold leading-6 text-gray-900">{{ $t('dialogs.two_factor_auth_dialog.enter_code_header') }}</span>
                </div>
            </template>            

            <div>
                {{ $t('dialogs.two_factor_auth_dialog.enter_code_instructions') }}
                <form class="space-y-6" @submit.prevent="submitLogin">
                    
                    <div class="flex justify-center gap-2 my-12">
                        <input
                            v-for="(box, index) in 6"
                            :key="index"
                            v-model="two_fa_code[index]"
                            type="text"
                            maxlength="1"
                            class="w-12 h-12 text-center text-xl border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @input="handleInput($event, index)"
                            @paste="handlePaste($event)"
                            @focus="this.codeErrors=null"
                            ref="inputBoxes"
                        />
                    </div>
                    <div class="text-red-500" v-show="codeErrors != null">{{ codeErrors }}</div>
                </form>
            </div>

            <template #footer>
                <!-- <Button :label="$t('common.cancel')" text severity="secondary" @click="cancel"/> -->
                <Button :label="$t('common.back')" outlined severity="secondary" @click="prevStep()" />
                <Button :label="$t('dialogs.two_factor_auth_dialog.verify_code')" @click="validateTwoFactorCode()" />
            </template>
            
        </Dialog>
        
        <Dialog v-model:visible="visible" modal :header="$t('dialogs.two_factor_auth_dialog.enabled_header')" v-if="step==4">

            <template #header>
                <div class="inline-flex align-items-center justify-content-center gap-2">
                    <!-- <IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/> -->
                    <span class="text-lg font-semibold leading-6 text-gray-900">{{ $t('dialogs.two_factor_auth_dialog.enabled_header') }}</span>
                </div>
            </template>            

            <div>
                <div class="flex items-center justify-center my-6">
                    <!-- <span class="text-green-500 text-8xl">✔️</span> -->
                     
                    <IconLockCheck class="w-40 h-40 mr-1.5 text-green-800" />
                </div>
                <strong>{{ $t('dialogs.two_factor_auth_dialog.setup_success') }}</strong><br />
                <br />
                {{ $t('dialogs.two_factor_auth_dialog.recovery_codes_instructions') }}<br />
                <br />
                <div id="recoveryCodes"></div>
            </div>

            <template #footer>
                <!-- <Button :label="$t('common.cancel')" text severity="secondary" @click="cancel"/> -->
                <Button :label="$t('dialogs.two_factor_auth_dialog.download_recovery_codes')" severity="secondary" @click="this.downloadRecoveryCodes()" />
                <Button :label="$t('dialogs.two_factor_auth_dialog.close')" @click="this.visible=false; this.step=1" />
            </template>
            
        </Dialog>
        
        <Dialog v-model:visible="visible" modal :header="$t('dialogs.two_factor_auth_dialog.disable_header')" v-if="step==5">

            <template #header>
                <div class="inline-flex align-items-center justify-content-center gap-2">
                    <!-- <IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/> -->
                    <span class="text-lg font-semibold leading-6 text-gray-900">{{ $t('dialogs.two_factor_auth_dialog.disable_header') }}</span>
                </div>
            </template>            

            <div>
                <div class="flex items-center justify-center my-6">
                    <!-- <span class="text-green-500 text-8xl">✔️</span> -->
                     
                    <IconLockOff class="w-40 h-40 mr-1.5 text-gray-300" />
                </div>
                {{ $t('dialogs.two_factor_auth_dialog.disable_confirmation') }}
            </div>

            <template #footer>
                <!-- <Button :label="$t('common.cancel')" text severity="secondary" @click="cancel"/> -->
                <Button :label="$t('dialogs.two_factor_auth_dialog.confirm_disable')" @click="disableTwoFactorAuth()" />
            </template>
            
        </Dialog>
        
        <Dialog v-model:visible="visible" modal :header="$t('dialogs.two_factor_auth_dialog.disabled_header')" v-if="step==6">

            <template #header>
                <div class="inline-flex align-items-center justify-content-center gap-2">
                    <!-- <IconUserCircle class="h-5 w-5 text-gray-400 mr-2"/> -->
                    <span class="text-lg font-semibold leading-6 text-gray-900">{{ $t('dialogs.two_factor_auth_dialog.disabled_header') }}</span>
                </div>
            </template>            

            <div>
                <div class="flex items-center justify-center my-6">
                    <!-- <span class="text-green-500 text-8xl">✔️</span> -->
                     
                    <IconLockOff class="w-40 h-40 mr-1.5 text-red-800" />
                </div>
                {{ $t('dialogs.two_factor_auth_dialog.disabled_message') }}
            </div>

            <template #footer>
                <!-- <Button :label="$t('common.cancel')" text severity="secondary" @click="cancel"/> -->
                <Button :label="$t('dialogs.two_factor_auth_dialog.close')" @click="this.visible=false" />
            </template>
            
        </Dialog>
    </div>
</template>

<script>
import { useUsersStore } from "@/stores/users.js";
import {useAuthStore} from "@/stores/auth.js";
import { mapStores } from "pinia";
import { IconUserCircle, IconLockCheck, IconLockOff } from '@tabler/icons-vue';

export default {
	name: "TwoFactorAuthDialog",
	components: {
		IconUserCircle,
        IconLockCheck, 
        IconLockOff
	},
	data() {
		return {
			visible: false,
            codeErrors: null,
            step:1,
            two_fa_code: [],
            two_fa_code_joined: '',
            recovery_codes: null
		}
	},
	computed: {
		...mapStores(useUsersStore),
        ...mapStores(useAuthStore),
	},
	methods: {
        prevStep(){
            this.step--;
            if(this.step==2){
                this.startTwoFactorAuth();
            }
        },
        nextStep(){
            this.step++;
            console.log('this.step',this.step);
            if(this.step==2){
                this.startTwoFactorAuth();
            }
            if(this.step==3){
                //focus first input box
                this.$nextTick(() => {
                    if (this.$refs.inputBoxes && this.$refs.inputBoxes[0]) {
                        this.$refs.inputBoxes[0].focus();
                    }
                });
            }
            if(this.step==4){
                this.show2FARecoveryCodes()
            }
        },
		async open() {
			this.form = null;
			// this.form = await this.$auth.editSelf();
			this.visible = true;
		},
		close() {
			this.visible = false;
		},
		async confirm() {
			await this.form.submit();
			window.location.reload();

			this.$emit('on-confirm');
			this.close();
		},
		cancel() {
			this.$emit('on-cancel');
			this.close();
		},
        async validateTwoFactorCode(){
            try {
                console.log('validateTwoFactorCode');
                this.two_fa_code_joined = this.two_fa_code.join('');
                const response = await axios.post("user/confirm-two-factor-authentication", {
                    // user_id: this.userId,
                    code: this.two_fa_code_joined,
                });
                console.log(response);
                if(response.status === 200){
                    // this.step = 4;
                    this.nextStep();
                    console.log('set 2fa enabled');
                    this.authStore.user.two_factor_enabled = true;
                }else{
                    console.log('error verifying 2fa code (1)');
                }
            } catch (error) {
                console.log('error');
                console.log(error);
                // console.log(error.response.data.errors);
                if(error.response.data.errors && error.response.data.errors['code']){
                    this.codeErrors = error.response.data.errors['code'][0];
                }
                console.error("Error verifying 2FA code", error);
            }
        },
        
        async startTwoFactorAuth() {
            console.log('startTwoFactorAuth');
        try {
            // Step 1: Enable Two-Factor Authentication
            await axios.post("user/two-factor-authentication");

            // Step 2: Fetch the QR code image data
            const response = await axios.get("user/two-factor-qr-code", {
                responseType: "blob", // Get binary data
            });

            // Step 3: Convert the response into an Object URL
            const imageUrl = URL.createObjectURL(response.data);

            // Step 4: Replace only the contents of the <main> element
            const mainElement = document.querySelector("#qr-code-container");
            if (mainElement) {
                
                const img = mainElement.querySelector("img");
                img.src = imageUrl;
                img.style.display = "none";
                img.style.margin = "0 auto"; // Center the image
                
                mainElement.appendChild(img);
                // simulate a slight delay
                setTimeout(() => {
                    img.style.display = "block";
                    document.querySelector("#qr-code-spinner-container").style.display = "none";
                }, 100);
            } else {
                // console.error("No <main> element found.");
            }
        } catch (error) {
            console.error("Error enabling 2FA or fetching QR code", error);
        }
        },
        async disableTwoFactorAuth(){
            try {
                const response = await axios.delete("user/two-factor-authentication/57", {
                // user_id: this.userId,
                });

                // If 2FA is successful, log in the user
                // alert("2FA deleted");
                this.step++;
                this.authStore.user.two_factor_enabled = false;
            } catch (e) {
                console.error("2FA delete failed", e);
            }
        },
        async show2FARecoveryCodes(){
            console.log('show2FARecoveryCodes');
            try{
                const response = await axios.get("user/two-factor-recovery-codes", {
                        // user_id: this.userId,
                    });
                    this.recovery_codes = response.data;
                    const recoveryCodesElement = document.querySelector("#recoveryCodes");
                    if (recoveryCodesElement) {
                        recoveryCodesElement.innerHTML = response.data
                            .map(code => `<div class="p-2 bg-gray-200 rounded my-1">${code}</div>`)
                            .join("");
                    }
                    
            } catch (error) {
                console.error("Error enabling 2FA or fetching QR code", error);
            }
        },
        handleInput(event, index) {
            const value = event.target.value;
            if (value.length > 1) {
                this.two_fa_code[index] = value[1];
            }
            if (value.length === 0) {
                this.two_fa_code[index] = "";
            }
            if (index < 5 && value.length === 1) {
                this.$refs.inputBoxes[index + 1].focus();
            }
            if(index == 5){
                // validate when last box is filled
                // this.validateTwoFactorCode();
            }
        },
        handlePaste(event) {
            event.preventDefault();
            const paste = event.clipboardData.getData("text");
            if (paste.length === 6 && /^\d+$/.test(paste)) {
                this.two_fa_code = paste.split("");
                this.$refs.inputBoxes[0].blur();
                // this.validateTwoFactorCode();
            }
        },
         downloadRecoveryCodes() {
            const blob = new Blob([this.recovery_codes.join('\n')], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'sums-2fa-recovery-codes.txt';
            a.click();
            URL.revokeObjectURL(url);
        }

	},
}
</script>
